import { format as fnsFormat, Locale } from "date-fns"
import { enUS, de } from "date-fns/locale"
import { Language, StorageKey } from "../enums"
import SessionService from "../SessionService"
import { Room } from "../../Types/Room"
import { RefObject, useEffect, useRef, useState } from "react"
export interface dateLanguage {
    english: Locale
    deutsch: Locale
}

const hasTimezoneInformation = (value: string) => {
    return /([zZ]|[+-]\d{2}(:?\d{2})?)$/.test(value)
}

const isDateTimeString = (value: string) => {
    return /[T\s]\d{2}:\d{2}/.test(value)
}

const isIsoLikeDateString = (value: string) => {
    return /^\d{4}-\d{2}-\d{2}/.test(value)
}

const LEGACY_UTC_DATE_REGEX = /^(\d{1,2})\s+([A-Za-zÄÖÜäöüß.]+)\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i

const getMonthIndex = (monthLabel: string) => {
    const normalizedMonthLabel = monthLabel
        .trim()
        .replace(".", "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")

    const monthMap: Record<string, number> = {
        jan: 0,
        januar: 0,
        feb: 1,
        februar: 1,
        mar: 2,
        maerz: 2,
        mrz: 2,
        april: 3,
        apr: 3,
        may: 4,
        mai: 4,
        jun: 5,
        juni: 5,
        jul: 6,
        juli: 6,
        aug: 7,
        august: 7,
        sep: 8,
        sept: 8,
        september: 8,
        oct: 9,
        okt: 9,
        oktober: 9,
        nov: 10,
        november: 10,
        dec: 11,
        dez: 11,
        dezember: 11,
        december: 11,
    }

    return monthMap[normalizedMonthLabel]
}

const parseLegacyUtcDateString = (value: string) => {
    const matches = value.match(LEGACY_UTC_DATE_REGEX)

    if (!matches) {
        return null
    }

    const [, day, monthLabel, year, hour12, minute, meridiem] = matches
    const monthIndex = getMonthIndex(monthLabel)

    if (monthIndex === undefined) {
        return null
    }

    const parsedHour12 = Number(hour12)
    const parsedMinute = Number(minute)
    const parsedDay = Number(day)
    const parsedYear = Number(year)

    if ([parsedHour12, parsedMinute, parsedDay, parsedYear].some(Number.isNaN)) {
        return null
    }

    const normalizedMeridiem = meridiem.toUpperCase()
    const hour24 = parsedHour12 % 12 + (normalizedMeridiem === "PM" ? 12 : 0)

    const parsedDate = new Date(Date.UTC(parsedYear, monthIndex, parsedDay, hour24, parsedMinute))

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const normalizeDateString = (value: string) => {
    let normalized = value.trim()

    if (!isIsoLikeDateString(normalized)) {
        return normalized
    }

    // Some APIs include a space before UTC offsets: "... 12:00:00 +00:00".
    normalized = normalized.replace(/\s+([+-]\d{2}(:?\d{2})?)$/, "$1")

    if (normalized.includes(" ")) {
        normalized = normalized.replace(" ", "T")
    }

    // Convert microseconds to milliseconds because JS Date/parseISO expect max 3 digits.
    normalized = normalized.replace(/\.(\d{3})\d+/, ".$1")

    if (isDateTimeString(normalized) && !hasTimezoneInformation(normalized)) {
        normalized = `${normalized}Z`
    }

    return normalized
}

const parseAsUtcWhenTimezoneMissing = (value: string | number | Date) => {
    if (typeof value !== "string") {
        return new Date(value)
    }

    const trimmedValue = value.trim()

    if (!trimmedValue) {
        return new Date(trimmedValue)
    }

    if (!isIsoLikeDateString(trimmedValue)) {
        const parsedLegacyUtcDate = parseLegacyUtcDateString(trimmedValue)

        if (parsedLegacyUtcDate) {
            return parsedLegacyUtcDate
        }

        return new Date(trimmedValue)
    }

    const normalizedValue = normalizeDateString(trimmedValue)
    const candidates = [normalizedValue, trimmedValue]

    if (isDateTimeString(normalizedValue) && !hasTimezoneInformation(normalizedValue)) {
        candidates.push(`${normalizedValue}Z`)
    }

    for (const candidate of candidates) {
        const parsedDate = new Date(candidate)
        if (!Number.isNaN(parsedDate.getTime())) {
            return parsedDate
        }
    }

    return new Date(normalizedValue)
}

export const formatDate = (date: string | number | Date, format: string) => {
    const locales = { [Language.english]: enUS, [Language.deutsch]: de } as dateLanguage
    const language = (SessionService.getItem(StorageKey.language) as string) || Language.deutsch

    const locale = locales[language as keyof dateLanguage]

    try {
        const parsedDate = parseAsUtcWhenTimezoneMissing(date)
        if (Number.isNaN(parsedDate.getTime())) {
            return null
        }

        return date ? fnsFormat(parsedDate, format, { locale }) : null
    } catch (err) {
        return null
    }
}

export const isDefined = (value: string | null | undefined) => {
    return value !== null && value !== undefined && value !== ""
}

export const capitalizeFirstLetter = (text: string) => {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : ""
}

export const openLinkInNewTab = () => {
    document.addEventListener("click", function (event: Event) {
        let element = event.target as HTMLElement
        const tagName = "A"
        if (element.tagName === tagName && !element.hasAttribute("target")) {
            element.setAttribute("target", "_blank")
            element.setAttribute("rel", "noreferrer")
        }
    })
}

export let ConvertStringToHTML = (string: string) => {
    let parser = new DOMParser()
    let doc = parser.parseFromString(string, "text/html")
    return doc.body
}

export const matchWord = (content: string, word: string) => {
    var result = new RegExp("\\b" + word + "\\b")
    return content.search(result)
}

export const addNewLine = (text: string) => {
    const newText = text?.split("\n").map((line, index) => (
        <p key={index} className={`${(line.length === 0 || /\s/.test(line)) && "new-line"}`}>
            {line}
        </p>
    ))
    return newText
}

export const convertBytesToUnits = (bytes: number) => {
    let units = ["B", "KB", "MB", "GB", "TB", "PB"]
    let i = 0

    for (i; bytes > 1024; i++) {
        bytes /= 1024
    }

    return bytes.toFixed(1) + " " + units[i]
}

export const documentNameLength = (name: string, screenWidth: number, breakpoint: number) => {
    if (screenWidth > breakpoint) {
        return name!.length > 25 ? name!.substring(0, 25) + "..." : name
    }
    return name!.length > 15 ? name!.substring(0, 15) + "..." : name
}

export const replaceHTMLCharactersWithCorrespondingLetters = (content: string) => {
    return content
        .replace(/&auml;/g, "ä")
        .replace(/&Auml;/g, "Ä")
        .replace(/&ouml;/g, "ö")
        .replace(/&Ouml;/g, "Ö")
        .replace(/&uuml;/g, "ü")
        .replace(/&Uuml;/g, "Ü")
        .replace(/&szlig;/g, "ß")
        .replace(/&agrave;/g, "à")
        .replace(/&Agrave;/g, "À")
        .replace(/&aacute;/g, "á")
        .replace(/&Aacute;/g, "Á")
        .replace(/&acirc;/g, "â")
        .replace(/&Acirc;/g, "Â")
        .replace(/&atilde;/g, "ã")
        .replace(/&Atilde;/g, "Ã")
        .replace(/&ccedil;/g, "ç")
        .replace(/&Ccedil;/g, "Ç")
        .replace(/&egrave;/g, "è")
        .replace(/&Egrave;/g, "È")
        .replace(/&eacute;/g, "é")
        .replace(/&Eacute;/g, "É")
        .replace(/&ecirc;/g, "ê")
        .replace(/&Ecirc;/g, "Ê")
        .replace(/&euml;/g, "ë")
        .replace(/&Euml;/g, "Ë")
        .replace(/&iuml;/g, "ï")
        .replace(/&Iuml;/g, "Ï")
        .replace(/&ocirc;/g, "ô")
        .replace(/&Ocirc;/g, "Ô")
        .replace(/&yuml;/g, "ÿ")
        .replace(/&Yuml;/g, "Ÿ")
        .replace(/&aelig;/g, "æ")
        .replace(/&AElig;/g, "Æ")
        .replace(/&ntilde;/g, "ñ")
        .replace(/&Ntilde;/g, "Ñ")
        .replace(/&uacute;/g, "ú")
        .replace(/&Uacute;/g, "Ú")
        .replace(/&#156;/g, "œ")
        .replace(/&#140;/g, "Œ")
        .replace(/&iacute;/g, "í")
        .replace(/&Iacute;/g, "Í")
        .replace(/&micro;/g, "µ")
}

export const arrayOfLength = (rooms: Room[]) => {
    let length = rooms?.map(room => room.posts?.length)
    return length
}

export default function useOnScreen(ref: RefObject<HTMLElement | null>) {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [isOnScreen, setIsOnScreen] = useState(false);
    useEffect(() => {
        observerRef.current = new IntersectionObserver(([entry]) =>
            setIsOnScreen(entry.isIntersecting)
        );

    }, []);

    useEffect(() => {
        if (ref.current instanceof Element) {
            observerRef.current?.observe(ref.current!);
            return () => {
                observerRef.current?.disconnect();
            };
        }

    }, [ref]);

    return isOnScreen;
}